import * as TabsPrimitive from "@radix-ui/react-tabs";
import type * as React from "react";
import { type RefObject, useLayoutEffect, useRef, useState } from "react";
import { cn } from "../utils";

function Tabs({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return <TabsPrimitive.Root data-slot="tabs" className={cn("flex min-w-0 flex-col gap-2", className)} {...props} />;
}

// The width the triggers need side by side, whatever layout they currently have.
const measureSideBySideWidth = (list: HTMLElement) => {
  const { flexDirection, width, maxWidth } = list.style;
  Object.assign(list.style, { flexDirection: "row", width: "max-content", maxWidth: "none" });
  const sideBySideWidth = list.offsetWidth;
  Object.assign(list.style, { flexDirection, width, maxWidth });
  return sideBySideWidth;
};

// True once the triggers no longer fit side by side in the space the list's container gives it.
const useTriggersCrowded = (listRef: RefObject<HTMLDivElement | null>) => {
  const [crowded, setCrowded] = useState(false);

  useLayoutEffect(() => {
    const list = listRef.current;
    const container = list?.parentElement;
    if (!list || !container) {
      return;
    }

    const measure = () => setCrowded(container.clientWidth < measureSideBySideWidth(list));

    measure();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [listRef]);

  return crowded;
};

function TabsList({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.List>) {
  const listRef = useRef<HTMLDivElement>(null);
  const crowded = useTriggersCrowded(listRef);

  return (
    <TabsPrimitive.List
      ref={listRef}
      data-slot="tabs-list"
      data-crowded={crowded || undefined}
      className={cn(
        "bg-muted text-muted-foreground inline-flex h-9 w-fit max-w-full items-center justify-center rounded-lg p-[3px]",
        // Triggers that would overlap side by side stack into a full-width column instead.
        "data-crowded:h-auto data-crowded:w-full data-crowded:flex-col data-crowded:items-stretch",
        className
      )}
      {...props}
    />
  );
}

function TabsTrigger({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "data-[state=active]:bg-background dark:data-[state=active]:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring dark:data-[state=active]:border-input dark:data-[state=active]:bg-input/30 text-foreground dark:text-muted-foreground inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:shadow-sm [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 cursor-pointer",
        className
      )}
      {...props}
    />
  );
}

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return <TabsPrimitive.Content data-slot="tabs-content" className={cn("flex-1 outline-none", className)} {...props} />;
}

export { Tabs, TabsContent, TabsList, TabsTrigger };
